import axios from 'axios';

// Always use relative /api path — the frontend calls the same domain it's served from.
export const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' }
});

export function setAuthToken(token) {
  localStorage.setItem('afii_token', token);
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

export function clearAuthToken() {
  localStorage.removeItem('afii_token');
  delete api.defaults.headers.common['Authorization'];
}

export function getAuthToken() {
  return localStorage.getItem('afii_token');
}

// Initialize token from storage
const savedToken = getAuthToken();
if (savedToken) {
  api.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
}

// Auth resilience: detect 401s globally, redirect to login with preserved destination
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const path = window.location.pathname;
      // Don't redirect if already on auth pages or public endpoints
      if (path !== '/auth' && path !== '/' && path !== '/courses' && !path.startsWith('/auth')) {
        clearAuthToken();
        const dest = encodeURIComponent(path);
        window.location.href = `/auth?redirect=${dest}`;
      }
    }
    return Promise.reject(error);
  }
);

// Streaming helper for Grace chat
export async function streamChat({ chatId, message, mode, lang, onDelta, onMeta, onError, onDone }) {
  const token = getAuthToken();
  const streamUrl = `/api/chats/${chatId}/stream`;
  const res = await fetch(streamUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ message, mode, lang: lang || 'en' })
  });

  if (!res.ok || !res.body) {
    if (res.status === 401) {
      clearAuthToken();
      window.location.href = '/auth?redirect=' + encodeURIComponent(window.location.pathname);
      return;
    }
    const text = await res.text().catch(() => '');
    throw new Error(text || 'Stream failed');
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';

    for (const part of parts) {
      const lines = part.split('\n').filter(Boolean);
      let eventName = 'message';
      let dataLine = null;

      for (const line of lines) {
        if (line.startsWith('event:')) eventName = line.slice(6).trim();
        if (line.startsWith('data:')) dataLine = line.slice(5).trim();
      }

      if (!dataLine) continue;
      let data = {};
      try { data = JSON.parse(dataLine); } catch { data = { raw: dataLine }; }

      if (eventName === 'delta') onDelta?.(data.delta || '');
      else if (eventName === 'meta') onMeta?.(data);
      else if (eventName === 'error') onError?.(data);
      else if (eventName === 'done') onDone?.(data);
    }
  }
}
